import React from 'react';
import { motion } from 'framer-motion';
import { whatsappHref as montarWhatsappHref, registrarCliqueWhatsApp } from '../../lib/origem';

interface HeroSectionProps {
  heroImageDesktop: string;
  heroImageMobile: string;
  handleScheduleClick: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({
  heroImageDesktop,
  heroImageMobile,
  handleScheduleClick,
}) => {
  const whatsappHref = montarWhatsappHref();

  const whatsappIcon = (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      className="w-5 h-5"
      aria-hidden="true"
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );

  return (
    <motion.section
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.9 }}
      aria-labelledby="hero-heading"
    >
      {/* ── MOBILE: foto empilhada acima do conteúdo ── */}
      <div className="md:hidden flex flex-col">
        {/* Foto */}
        <div className="relative w-full" style={{ paddingBottom: '110%' }}>
          <img
            src={heroImageMobile}
            alt="Eliane Forti Fernandes, psicóloga"
            className="absolute inset-0 w-full h-full object-cover object-top"
            loading="eager"
          />
          {/* gradiente suave na borda inferior da foto para fazer transição */}
          <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-b from-transparent to-[#111c32]" />
        </div>

        {/* Conteúdo sobre fundo escuro */}
        <div className="bg-[#111c32] px-5 pt-6 pb-10">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="inline-flex items-center rounded-full border border-white/30 bg-white/10 px-4 py-2 text-xs tracking-[0.18em] uppercase text-white/90"
          >
            Psicoterapia online e presencial
          </motion.span>

          <h1
            id="hero-heading"
            className="text-[1.75rem] leading-[1.2] font-semibold mt-4 mb-4 text-white"
          >
            Um espaço ético para você ser escutado com profundidade
          </h1>

          <p className="text-base mb-7 text-white/85 leading-relaxed">
            Atendimento para adolescentes e adultos com uma escuta cuidadosa, sem julgamentos e sem respostas prontas. A proposta é construir, no seu tempo, caminhos mais conscientes para a sua história.
          </p>

          <div className="flex flex-col gap-3">
            <a
              href="#contato"
              onClick={handleScheduleClick}
              className="inline-flex items-center justify-center rounded-lg bg-white px-7 py-4 text-[#1e2f50] font-semibold shadow-lg transition duration-300 active:scale-95"
              aria-label="Clique para agendar sua consulta"
            >
              Agendar primeira conversa
            </a>
            <a
              href={whatsappHref}
              onClick={() => registrarCliqueWhatsApp('hero')}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/35 bg-white/10 px-7 py-4 text-white font-medium transition duration-300 active:scale-95"
            >
              {whatsappIcon}
              Falar no WhatsApp
            </a>
          </div>

          <div className="mt-6 grid grid-cols-1 gap-2">
            <div className="rounded-lg border border-white/20 bg-white/8 px-4 py-3 text-sm text-white/85">Especialização Einstein</div>
            <div className="rounded-lg border border-white/20 bg-white/8 px-4 py-3 text-sm text-white/85">CRP 06/216637</div>
            <div className="rounded-lg border border-white/20 bg-white/8 px-4 py-3 text-sm text-white/85">Atendimento online ou presencial em Caieiras/SP</div>
          </div>
        </div>
      </div>

      {/* ── DESKTOP: layout original com background-image ── */}
      <div className="hidden md:flex relative min-h-[100svh] overflow-hidden w-full">
        <div
          className="absolute inset-0 z-0 w-full"
          style={{
            backgroundImage: `url(${heroImageDesktop})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
          role="img"
          aria-label="Imagem de fundo representando ambiente acolhedor de terapia"
        />
        <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/56 via-black/38 to-black/18" />
        <div className="absolute inset-y-0 left-0 w-[72%] bg-gradient-to-r from-[#111c32]/78 via-[#162748]/44 to-transparent z-10" />
        <div className="absolute inset-y-0 left-0 w-[58%] bg-[radial-gradient(circle_at_14%_30%,rgba(255,255,255,0.11),transparent_50%)] z-10" />

        <div className="relative z-20 w-full min-h-[100svh] flex items-center">
          <div className="w-full max-w-7xl mx-auto px-6 lg:px-8 py-28">
            <div className="w-[68%] lg:w-[56%] xl:w-[52%]">
              <motion.span
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className="inline-flex items-center gap-2 rounded-full border border-white/35 bg-white/10 px-4 py-2 text-sm tracking-[0.18em] uppercase text-white/95 backdrop-blur-sm"
              >
                Psicoterapia online e presencial
              </motion.span>

              <h1
                id="hero-heading"
                className="text-4xl leading-[1.05] md:text-5xl lg:text-6xl font-semibold mt-5 mb-6 text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]"
              >
                Um espaço ético para você ser escutado com profundidade
              </h1>

              <p className="text-lg md:text-xl mb-9 text-white/95 max-w-2xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.35)]">
                Atendimento para adolescentes e adultos com uma escuta cuidadosa, sem julgamentos e sem respostas prontas. A proposta é construir, no seu tempo, caminhos mais conscientes para a sua história.
              </p>

              <div className="flex flex-row gap-4">
                <a
                  href="#contato"
                  onClick={handleScheduleClick}
                  className="inline-flex items-center justify-center rounded-lg bg-white px-7 py-4 text-[#1e2f50] font-semibold shadow-[0_10px_35px_rgba(0,0,0,0.28)] transition duration-300 hover:-translate-y-0.5 hover:bg-[#f6f8ff]"
                  aria-label="Clique para agendar sua consulta"
                >
                  Agendar primeira conversa
                </a>
                <a
                  href={whatsappHref}
                  onClick={() => registrarCliqueWhatsApp('hero')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border border-white/45 bg-white/10 px-7 py-4 text-white font-medium backdrop-blur-sm transition duration-300 hover:bg-white/20"
                >
                  {whatsappIcon}
                  Falar no WhatsApp
                </a>
              </div>

              <div className="mt-8 grid grid-cols-3 gap-3">
                <div className="rounded-lg border border-white/25 bg-white/10 px-4 py-3 text-sm text-white/95 backdrop-blur-sm">Especialização Einstein</div>
                <div className="rounded-lg border border-white/25 bg-white/10 px-4 py-3 text-sm text-white/95 backdrop-blur-sm">CRP 06/216637</div>
                <div className="rounded-lg border border-white/25 bg-white/10 px-4 py-3 text-sm text-white/95 backdrop-blur-sm">Atendimento online ou presencial em Caieiras/SP</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default HeroSection;

